/**
 * <shop-panel> — behaviour for snippets/header-shop-panel.liquid.
 *
 * Open/close itself is Dawn's <header-menu> + <details> (click/tap toggle, Escape, focus-out).
 * This adds:
 * - mouse-only hover open, closing 150ms after the pointer leaves the trigger + panel
 *   (unless keyboard focus is inside the panel);
 * - closing on a click/tap on the page dim (#MainContent::after, component-shop-panel.css);
 * - switching the visible collection preview on category hover (mouse) or focus;
 * - eager-loading every panel image once the panel opens, so hidden previews are ready.
 */
class ShopPanel extends HTMLElement {
  constructor() {
    super();
    this.menu = this.closest('header-menu');
    this.details = this.closest('details');
    this.hovering = false;

    this.querySelectorAll('[data-shop-panel-target]').forEach((link) => {
      link.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.show(link));
      link.addEventListener('focus', () => this.show(link));
    });

    this.menu.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.hovering = true;
      clearTimeout(this.closeTimer);
      this.open();
    });
    this.menu.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.hovering = false;
      this.closeTimer = setTimeout(() => {
        // :focus-visible = keyboard focus; a mouse click on the trigger also focuses it but shouldn't pin the panel open.
        if (!this.menu.querySelector(':focus-visible')) this.menu.close();
      }, 150);
    });

    // A click on the trigger while hover already opened the panel would toggle it shut under the pointer.
    // Capture phase runs before Dawn's summary click handler (aria-expanded), so stop it there too.
    this.details.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('summary');
        if (!this.hovering || !this.details.open || summary?.parentElement !== this.details) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // The open dim is #MainContent's ::after, so a click on it targets #MainContent itself.
    document.getElementById('MainContent')?.addEventListener('click', (event) => {
      if (!this.details.open || event.target.id !== 'MainContent') return;
      event.preventDefault();
      this.menu.close();
    });

    this.details.addEventListener('toggle', () => {
      if (!this.details.open) return;
      // HeaderMenu measures the header bottom only on the first open; the sticky header (reduce-logo-size) changes
      // height with scroll, so re-measure on every open to keep .mega-menu__content's max-height right.
      if (this.menu.header) {
        document.documentElement.style.setProperty(
          '--header-bottom-position-desktop',
          `${Math.floor(this.menu.header.getBoundingClientRect().bottom)}px`
        );
      }
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    });
  }

  open() {
    if (this.details.open) return;
    this.details.open = true;
    this.details.querySelector('summary').setAttribute('aria-expanded', true);
  }

  show(link) {
    const targetId = link.dataset.shopPanelTarget;
    this.querySelectorAll('.shop-panel__preview').forEach((preview) => (preview.hidden = preview.id !== targetId));
    this.querySelectorAll('[data-shop-panel-target]').forEach((item) =>
      item.classList.toggle('shop-panel__category--active', item === link)
    );
  }
}

customElements.define('shop-panel', ShopPanel);
